/**
 * El marco del portal: cabecera, pestañas y poco más.
 *
 * Nada del panel del personal entra aquí. El paciente no necesita un menú
 * lateral de doce secciones: tiene tres cosas que mirar y las quiere ver desde
 * el teléfono, así que las pestañas van arriba y grandes.
 */
import { useEffect } from "react";
import { NavLink, Navigate, Outlet } from "react-router-dom";
import {
  CalendarDays,
  FlaskConical,
  LogOut,
  Pill,
  Settings,
  User,
} from "lucide-react";
import { usePortalAuth } from "../../auth/PortalAuthProvider";
import { useTenantBranding, useBase } from "../../auth/TenantBrandingProvider";

export function PortalLayout() {
  // Con dominio propio no hay slug en la URL y la base es "": los enlaces
  // del portal quedan `/portal/citas` en vez de `/mi-salud/portal/citas`.
  const base = useBase();
  const { estado, me, salir } = usePortalAuth();
  const { branding } = useTenantBranding();

  // El título de la pestaña del navegador también es del policlínico: el
  // paciente suele tener varias abiertas.
  useEffect(() => {
    if (branding?.nombre_comercial) {
      document.title = `${branding.nombre_comercial} · Mi salud`;
    }
  }, [branding?.nombre_comercial]);

  if (estado === "cargando") {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-slate-400">
        Cargando…
      </div>
    );
  }
  if (estado === "fuera") return <Navigate to={`${base}/portal/entrar`} replace />;

  const tabs = [
    { to: `${base}/portal`, label: "Inicio", icon: User, end: true },
    { to: `${base}/portal/citas`, label: "Mis citas", icon: CalendarDays },
    { to: `${base}/portal/recetas`, label: "Mis recetas", icon: Pill },
    { to: `${base}/portal/resultados`, label: "Resultados", icon: FlaskConical },
    { to: `${base}/portal/mis-datos`, label: "Mis datos", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-3 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900">
              {branding?.nombre_comercial ?? me?.policlinico}
            </p>
            <p className="truncate text-xs text-slate-500">
              {me?.yo.nombre_completo}
            </p>
          </div>
          <button
            type="button"
            onClick={salir}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            <LogOut className="h-4 w-4" />
            Salir
          </button>
        </div>

        <nav className="mx-auto flex max-w-3xl gap-1 overflow-x-auto px-2 pb-2">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                className={({ isActive }) =>
                  `inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition ${
                    isActive
                      ? "bg-[color:var(--color-link)] text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </NavLink>
            );
          })}
        </nav>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
